import React, { useState } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Paper,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Psychology as PsychologyIcon,
  Code as CodeIcon,
  Architecture as ArchitectureIcon,
  ContentCopy as ContentCopyIcon,
  Token as TokenIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { COLORS } from '../../shared/colors';
import { AI_PROMPT_PHASES } from '../../shared/appConstants';
import './AIDeveloperComponents.scss';

const AIPromptTracker = ({ promptData = [], compact = false }) => {
  const [expandedPhase, setExpandedPhase] = useState(null);
  const [copiedPromptId, setCopiedPromptId] = useState(null);

  const handlePhaseToggle = (phaseId) => {
    setExpandedPhase(expandedPhase === phaseId ? null : phaseId);
  };

  const handleCopyPrompt = async (prompt) => {
    try {
      await navigator.clipboard.writeText(prompt.content);
      setCopiedPromptId(prompt.id);
      setTimeout(() => setCopiedPromptId(null), 2000);
    } catch (error) {
      setCopiedPromptId(null);
    }
  };

  const getPhaseIcon = (phase) => {
    switch (phase) {
      case AI_PROMPT_PHASES.PLANNING: return <PsychologyIcon sx={{ color: COLORS.primary }} />;
      case AI_PROMPT_PHASES.CODE_GENERATION: return <CodeIcon sx={{ color: COLORS.success }} />;
      case AI_PROMPT_PHASES.CONTEXT_INITIALIZATION: return <ArchitectureIcon sx={{ color: COLORS.warning }} />;
      default: return <PsychologyIcon sx={{ color: COLORS.gray[500] }} />;
    }
  };




  const getTotalTokens = () => {
    if (!Array.isArray(promptData)) return 0;
    return promptData.reduce((total, phase) => {
      if (!Array.isArray(phase.prompts)) return total;
      return total + phase.prompts.reduce((phaseTotal, prompt) => {
        return phaseTotal + (prompt.tokens || 0);
      }, 0);
    }, 0);
  };

  const getTotalPrompts = () => {
    if (!Array.isArray(promptData)) return 0;
    return promptData.reduce((total, phase) => {
      if (!Array.isArray(phase.prompts)) return total;
      return total + phase.prompts.length;
    }, 0);
  };



  if (!promptData || promptData.length === 0) {
    return (
      <Box className="ai-prompt-tracker">
        <div className="empty-state">
          <PsychologyIcon className="empty-icon" />
          <Typography variant="h6" color="text.secondary" className="empty-title">
            No AI Prompts Used Yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            AI prompts will appear here once the AI Developer starts working on this task.
          </Typography>
        </div>
      </Box>
    );
  }

  return (
    <Box className={`ai-prompt-tracker ${compact ? 'compact' : ''}`}>
      {/* Summary Statistics */}
      {promptData.length > 0 && !compact && (
        <Paper className="summary-paper">
          <Typography variant="subtitle1" className="summary-title">
            AI Development Summary
          </Typography>
          <div className="summary-stats">
            <div className="stat-item">
              <PsychologyIcon className="stat-icon primary" />
              <Typography variant="body2" color="text.secondary">
                <strong>{getTotalPrompts()}</strong> prompts used
              </Typography>
            </div>
            {getTotalTokens() > 0 && (
              <div className="stat-item">
                <TokenIcon className="stat-icon warning" />
                <Typography variant="body2" color="text.secondary">
                  <strong>{getTotalTokens().toLocaleString()}</strong> tokens consumed
                </Typography>
              </div>
            )}

          </div>
        </Paper>
      )}
      
      {Array.isArray(promptData) && promptData.map((phaseData) => (
        <Accordion 
          key={phaseData.phase}
          expanded={expandedPhase === phaseData.phase}
          onChange={() => handlePhaseToggle(phaseData.phase)}
          className="phase-accordion"
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            className="accordion-summary"
          >
            <div className="phase-header">
              {getPhaseIcon(phaseData.phase)}
              <div className="phase-content">
                <Typography variant="subtitle1" className="phase-title">
                  {phaseData.phaseName}
                </Typography>
                <Typography variant="caption" color="text.secondary" className="phase-subtitle">
                  {phaseData.prompts.length} prompt{phaseData.prompts.length !== 1 ? 's' : ''} used
                </Typography>

              </div>

            </div>
          </AccordionSummary>
          
          <AccordionDetails sx={{ p: 0 }}>
            {Array.isArray(phaseData.prompts) && phaseData.prompts.map((prompt, index) => (
              <Paper 
                key={prompt.id}
                className="prompt-card"
              >
                <div className="prompt-header">
                  <div className="prompt-info">
                    <Typography variant="subtitle2" className="prompt-title">
                      Template Used
                    </Typography>

                  </div>
                  <div className="prompt-actions">
                    <Tooltip title={copiedPromptId === prompt.id ? "Copied!" : "Copy Prompt"}>
                      <IconButton 
                        size="small" 
                        onClick={() => handleCopyPrompt(prompt)}
                        className={`copy-button ${copiedPromptId === prompt.id ? 'copied' : ''}`}
                        sx={{ width: 28, height: 28 }}
                      >
                        {copiedPromptId === prompt.id ? (
                          <CheckCircleIcon />
                        ) : (
                          <ContentCopyIcon />
                        )}
                      </IconButton>
                    </Tooltip>
                  </div>
                </div>
                
                {prompt.tokens && (
                  <div className="prompt-chips">
                    <div className="chips-container">
                      <Chip
                        icon={<TokenIcon className="chip-icon" />}
                        label={`${prompt.tokens} tokens`}
                        size="small"
                        className="chip-tokens"
                      />
                    </div>
                  </div>
                )}
                
                <Paper 
                  className="prompt-content-container"
                >
                  <Typography 
                    variant="body2" 
                    className="prompt-text"
                  >
                    {prompt.content}
                  </Typography>
                </Paper>
                

              </Paper>
            ))}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default AIPromptTracker;